import { Link } from "react-router-dom";
import { Shield, Mail, ArrowLeft } from "lucide-react";

/**
 * Public /privacy page.
 * Required for App Store / Google Play submission (Apple Guideline 5.1.1(iv),
 * Google Play Data safety section). Reviewers open this URL directly, so it
 * must be reachable without auth.
 *
 * Contact email is read from REACT_APP_SUPPORT_EMAIL if present, otherwise
 * falls back to a safe default that the founder can swap by setting the env
 * var before the release build.
 */
const SUPPORT_EMAIL =
  process.env.REACT_APP_SUPPORT_EMAIL || "support@withdrawguard.com";
const LAST_UPDATED = "February 9, 2026";

function Section({ id, title, children }) {
  return (
    <section id={id} className="mt-10" data-testid={`privacy-section-${id}`}>
      <h2 className="font-serif text-2xl md:text-3xl text-ink">{title}</h2>
      <div className="mt-4 space-y-3 text-[15px] leading-relaxed text-[#3A4740]">
        {children}
      </div>
    </section>
  );
}

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-2 md:gap-6 py-3 border-b border-[#EAE4DA] last:border-b-0">
      <div className="text-xs uppercase tracking-[0.15em] font-semibold text-sage pt-1">
        {label}
      </div>
      <div className="text-[15px] leading-relaxed text-[#3A4740]">{children}</div>
    </div>
  );
}

export default function Privacy() {
  return (
    <div className="min-h-screen bg-cream text-ink" data-testid="privacy-page">
      {/* Header */}
      <header className="border-b border-sand">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-6 flex items-center justify-between">
          <Link
            to="/"
            className="flex items-center gap-2 text-ink hover:text-forest transition-colors"
            data-testid="privacy-home-link"
          >
            <Shield className="w-4 h-4" strokeWidth={1.5} />
            <span className="font-serif text-lg">Withdraw Guard</span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-moss hover:text-ink transition-colors"
            data-testid="privacy-back-link"
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} /> Back
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 lg:px-10 py-14">
        {/* Title block */}
        <div className="mb-2 text-xs uppercase tracking-[0.2em] font-semibold text-sage">
          Legal
        </div>
        <h1 className="font-serif text-4xl md:text-5xl leading-[1.1] text-ink">
          Privacy Policy
        </h1>
        <p className="mt-4 text-[15px] text-moss">
          Effective {LAST_UPDATED}. Withdraw Guard is a lifestyle banking app —
          locks, bill locks, streaks and a city map. This policy explains, in plain
          English, what we collect (including the address you save for the Map),
          why we collect it, and every way you can turn it off or take it back.
        </p>

        {/* Summary card */}
        <div
          className="mt-8 bg-white border border-sand rounded-2xl p-6 shadow-[0_1px_0_rgba(26,37,32,0.04)]"
          data-testid="privacy-summary-card"
        >
          <div className="text-xs uppercase tracking-[0.2em] font-semibold text-forest">
            The 60-second version
          </div>
          <ul className="mt-4 space-y-2 text-[15px] leading-relaxed text-ink list-disc pl-5">
            <li>We do not sell your personal information. Not for money, not for barter.</li>
            <li>
              We collect only what we need to run a savings account: your name,
              email, password (hashed), and the bank details our regulated bank
              partner requires by law.
            </li>
            <li>
              Your money sits in custodian accounts at our banking provider
              partner — not on our servers.
            </li>
            <li>
              You can export your data or delete your account any time by emailing{" "}
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="underline decoration-gold underline-offset-4 hover:text-forest"
                data-testid="privacy-summary-email"
              >
                {SUPPORT_EMAIL}
              </a>
              . We answer within 7 days.
            </li>
          </ul>
        </div>

        {/* Sections */}
        <Section id="who-we-are" title="1. Who we are">
          <p>
            "Withdraw Guard," "we," "our," and "us" refer to the operators of
            the Withdraw Guard app and website. We are a US-based fintech that
            helps people save money by adding intentional friction to
            withdrawals.
          </p>
          <p>
            Withdraw Guard is not a bank. Deposits are held in
            custodian accounts through our banking provider. When you
            deposit or withdraw, we are acting as your instruction-passer to
            that bank — the money never sits on our servers.
          </p>
        </Section>

        <Section id="what-we-collect" title="2. What we collect and why">
          <div className="mt-2">
            <Row label="Account basics">
              Name, email address, hashed password (bcrypt), date of birth, and
              — if you enable it — a 2-factor authentication secret. Used to
              create your account, sign you in, and verify you are old enough
              to open a US financial account.
            </Row>
            <Row label="Financial identity">
              For US law compliance (Bank Secrecy Act and Know-Your-Customer
              rules), our bank partner requires legal name, address, date of
              birth, and Social Security Number. We collect these on the bank
              partner's behalf and pass them straight through. We never sell,
              display, or use this data for advertising.
            </Row>
            <Row label="Money movement">
              Deposits, withdrawals, vault transfers, budget categories, and
              scheduled payday commitments. Used to run the product you paid for
              and to generate your statements and tax documents.
            </Row>
            <Row label="Uploaded documents">
              If you use the Emergency Job Loss Vault, you upload proof of
              unemployment. We send these documents to a third-party AI
              verification service (see §4) and delete the raw file from our
              servers within 90 days of a successful or failed verification.
            </Row>
            <Row label="Support">
              When you email us or submit a ticket, we keep the message so we
              can help you. We delete support tickets 24 months after the last
              reply.
            </Row>
            <Row label="Map address">
              The street address you enter on the Map page, its coordinates, and the
              city/state we resolve from it. Used only to centre your map and find nearby
              places; sent to OpenStreetMap&apos;s Nominatim geocoder without your name or
              account details. Delete it any time with &quot;Forget my address&quot; on the Map.
            </Row>
            <Row label="Life-choice quiz">
              Your answers to the optional Level-Up quiz (income, housing, transport, goals)
              and the advice generated from them by our AI provider. Used only to personalise
              your suggestions; never shared with advertisers or the bank partner.
            </Row>
            <Row label="Device & log data">
              IP address, browser/device type, page timing, and errors. Used to
              fight fraud, debug outages, and stop abuse. Kept 30 days.
            </Row>
            <Row label="Cookies">
              We use a first-party cookie (or localStorage key) to keep you
              signed in. We do not run advertising, analytics-based
              retargeting, or "share this token with everyone" trackers.
            </Row>
          </div>
        </Section>

        <Section id="how-we-use-it" title="3. How we use your information">
          <ul className="list-disc pl-5 space-y-2">
            <li>To run the account you asked us to run — moving money, sending you statements, honoring cooling-off periods.</li>
            <li>To comply with US financial regulations (KYC, AML, tax reporting).</li>
            <li>To protect you and other users from fraud, unauthorized access, and abuse.</li>
            <li>To send you transactional email (receipts, security alerts, statements). We do not send promotional email without your explicit opt-in.</li>
            <li>To improve the product — but only in aggregated, deidentified form. We do not train AI on your personal data.</li>
          </ul>
          <p className="mt-2">
            We will never use your data to underwrite loans, sell insurance,
            profile you for advertisers, or run credit checks that you did not
            explicitly request.
          </p>
        </Section>

        <Section id="third-parties" title="4. Third parties we share with">
          <p>
            We only share data with vendors who need it to run the service. Each
            of these has a signed data-protection agreement that requires them
            to protect your data at least as strictly as we do.
          </p>
          <div className="mt-4">
            <Row label="Our bank partner">
              A US banking provider. Holds all customer funds in
              custodian accounts, issues debit cards, and executes ACH
              transfers. Receives your legal identity + financial account
              details.
            </Row>
            <Row label="Stripe & PayPal">
              Process one-time lifetime-access payments. We do not store your
              full card number — Stripe and PayPal do, under PCI-DSS. Receives
              your name, email, and billing amount.
            </Row>
            <Row label="Resend">
              Sends transactional email (receipts, statements, security
              alerts). Receives the email address and message body only.
            </Row>
            <Row label="Anthropic (Claude)">
              Reviews unemployment-verification documents you upload to the Job
              Loss Vault. Anthropic does not train on customer data by policy
              and deletes inputs within 30 days.
            </Row>
            <Row label="Hosting provider">
              Runs our servers and database. Encrypted at rest and in transit.
            </Row>
          </div>
          <p className="mt-3">
            <strong className="text-ink">Selling data:</strong> We do not
            sell your personal information, and we have not in the last 12
            months. We will not start.
          </p>
        </Section>

        <Section id="retention" title="5. How long we keep it">
          <ul className="list-disc pl-5 space-y-2">
            <li><strong>Account records</strong> — while your account is open, plus 7 years after closure (required by US federal financial recordkeeping law).</li>
            <li><strong>Transaction history</strong> — 7 years, same reason.</li>
            <li><strong>Uploaded verification documents</strong> — 90 days after the verification decision.</li>
            <li><strong>Support tickets</strong> — 24 months after the last reply.</li>
            <li><strong>Server logs</strong> — 30 days.</li>
          </ul>
        </Section>

        <Section id="your-rights" title="6. Your rights">
          <p>
            No matter where you live, you can:
          </p>
          <ul className="list-disc pl-5 space-y-2 mt-2">
            <li><strong>See a copy</strong> of the personal data we hold on you.</li>
            <li><strong>Correct</strong> anything that's wrong.</li>
            <li><strong>Download</strong> your statements and tax documents as CSV, any time, from inside the app.</li>
            <li><strong>Delete your account.</strong> We will close the account and delete data that isn't required by law to keep (see §5). Legally-required records stay locked and access-restricted until their retention period ends.</li>
            <li><strong>Turn off marketing email</strong> — the unsubscribe link is in every message. Transactional email (receipts, security alerts) can't be turned off while your account is active.</li>
          </ul>
          <p className="mt-3">
            To exercise any of these rights, email{" "}
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="underline decoration-gold underline-offset-4 hover:text-forest"
              data-testid="privacy-rights-email"
            >
              {SUPPORT_EMAIL}
            </a>
            . We respond within 7 days and complete the request within 30 days,
            or 45 days for complex cases (California/GDPR limits).
          </p>

          <h3 className="font-serif text-xl mt-6 text-ink">California residents (CCPA/CPRA)</h3>
          <p>
            You have the additional right to know exactly which categories of
            personal information we've collected in the past 12 months, and to
            direct us not to sell or share your data. We do not sell or share
            for cross-context advertising. Contact us at the email above to
            exercise any CCPA right; we will not discriminate against you for
            doing so.
          </p>

          <h3 className="font-serif text-xl mt-6 text-ink">EU / UK residents (GDPR / UK-GDPR)</h3>
          <p>
            Our legal basis for processing is (a) performance of the account
            contract you signed up for, (b) compliance with US financial legal
            obligations, and (c) our legitimate interest in preventing fraud.
            You have the right to lodge a complaint with your local Data
            Protection Authority if you believe we've mishandled your data. We
            do not currently offer a service tailored to EU/UK residents; if
            you signed up from these regions, contact us and we will delete
            your account and any associated personal data.
          </p>
        </Section>

        <Section id="security" title="7. How we protect it">
          <ul className="list-disc pl-5 space-y-2">
            <li>Passwords are hashed with bcrypt — we never see or store your actual password.</li>
            <li>Data in transit is protected with TLS 1.2+; data at rest is encrypted on our hosting provider.</li>
            <li>Two-factor authentication is available on every account (Profile → Security).</li>
            <li>Destructive administrative actions require a separate admin PIN and are audit-logged.</li>
            <li>We do not store full card numbers, full SSNs (they pass through to the bank partner), or biometric data.</li>
          </ul>
          <p className="mt-2">
            No online service is 100% secure. If we ever detect unauthorized
            access to your data, we will notify you within 72 hours of
            confirming the incident, per US state breach-notification laws.
          </p>
        </Section>

        <Section id="children" title="8. Children">
          <p>
            Withdraw Guard is not for children under 18. We do not knowingly
            collect data from anyone under 18. If we learn we've collected data
            from a minor, we delete it immediately. If you are a parent and
            believe your child has created an account, email us at{" "}
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="underline decoration-gold underline-offset-4 hover:text-forest"
            >
              {SUPPORT_EMAIL}
            </a>
            .
          </p>
        </Section>

        <Section id="international" title="9. International transfers">
          <p>
            Withdraw Guard is operated from the United States. If you sign up
            from outside the US, your data will be transferred to and processed
            in the US, which may have different data-protection rules than your
            home country. By signing up you consent to this transfer.
          </p>
        </Section>

        <Section id="changes" title="10. Changes to this policy">
          <p>
            If we materially change this policy, we will email every active
            user at least 30 days before the change takes effect. Minor
            wording, formatting, or typo fixes are made silently — the
            "effective" date at the top always reflects the latest edit.
          </p>
        </Section>

        <Section id="contact" title="11. How to reach us">
          <p>
            The fastest way to reach a real human is email:
          </p>
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="mt-3 inline-flex items-center gap-2 border border-ink rounded-lg px-5 py-3 text-sm font-medium text-ink hover:bg-ink hover:text-cream transition-colors"
            data-testid="privacy-contact-cta"
          >
            <Mail className="w-4 h-4" strokeWidth={1.5} /> {SUPPORT_EMAIL}
          </a>
          <p className="mt-4 text-sm text-sage">
            Postal mail: available on request via the email above (we do not
            publish the address to reduce spam).
          </p>
        </Section>
      </main>

      <footer className="border-t border-sand py-10 mt-10">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-moss">
            <Shield className="w-4 h-4" strokeWidth={1.5} />
            <span className="font-serif text-base">Withdraw Guard</span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/terms" className="text-xs uppercase tracking-[0.2em] font-semibold text-moss hover:text-ink transition-colors" data-testid="privacy-footer-terms-link">Terms</Link>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">
              © Withdraw Guard · Updated {LAST_UPDATED}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
